import { useQuery } from '@tanstack/react-query'
import AnalyticsApi from '@/infrastructure/api/analytics'
import { useAnalyticsScope } from '@/hooks'
import { Filter } from '@/domain/analytics'
import { useCountries } from '@/application/queries'
import { Country } from '@/domain/countries'

function getQueryKey(
  ecommerceIds: string[],
  startDate?: string,
  endDate?: string
) {
  return ['analyticsFilters', ecommerceIds, startDate, endDate]
}

export function useAnalyticsFilters(startDate?: string, endDate?: string) {
  const { ecommerceIds } = useAnalyticsScope()
  const { countries, loadingCountries } = useCountries()

  function callGetAvailableFilters() {
    if (!ecommerceIds.length) return
    return AnalyticsApi.getAnalyticsFilters({
      ecommerceId: ecommerceIds[0],
      ecommerceIds,
      startDate,
      endDate
    })
  }

  const analyticsFiltersQuery = useQuery({
    queryKey: getQueryKey(ecommerceIds, startDate, endDate),
    queryFn: callGetAvailableFilters,
    staleTime: 1000 * 60 * 15,
    enabled: ecommerceIds.length > 0 && !!startDate && !!endDate && !!countries
  })

  return {
    filters: transformFilters(analyticsFiltersQuery.data ?? [], countries),
    loadingFilters: analyticsFiltersQuery.isLoading || loadingCountries
  }
}

function transformFilters(filters: Filter[], countries: Country[]): Filter[] {
  return filters.map((f) => {
    const key = Object.keys(f)[0]
    if (key === 'countries')
      return { [key]: transformCountryValues(f[key], countries) }
    return f
  })
}

function transformCountryValues(
  countryCodes: string[],
  countries: Country[]
): string[] {
  return countryCodes.map((iso) => {
    return countries.find((c) => c.iso === iso)?.name ?? iso
  })
}
